import { Plus } from "lucide-react";
import { AttachControls, AttachmentList, PD_REMINDER } from "@/components/Attachments";
import { StatusDot, STATUS_LABEL, statusOf } from "@/components/StatusDot";
import { cn } from "@/lib/cn";
import { useBriefActions, type Attachment, type Section } from "@/lib/briefs";

/**
 * The left column: everything about the sections. One entry per box with its
 * status, "Overview" for the brief at a glance, "Add section", and the files
 * that belong to the brief as a whole. Picking a box shows it in the workspace
 * and focuses the interview on it. Desktop only: below `lg` the
 * SectionPicker stands in for it, and the files move into the overview.
 */
export function SectionRail({
  briefId,
  sections,
  looseAttachments,
  shownId,
  onPick,
  onAdd,
  canEdit,
}: {
  briefId: string;
  sections: Section[];
  looseAttachments: Attachment[];
  shownId: string | null;
  onPick: (id: string | null) => void;
  onAdd: () => void;
  canEdit: boolean;
}) {
  const actions = useBriefActions(briefId);
  const item = (active: boolean) =>
    cn(
      "flex items-center gap-2 rounded-md text-left text-sm transition-colors",
      "w-full px-2.5 py-2",
      active ? "bg-accent text-accent-foreground" : "text-muted-foreground hover:bg-secondary hover:text-foreground",
    );

  const list = (
    <nav aria-label="Sections" className="flex flex-col gap-0.5">
      <button type="button" className={item(shownId === null)} onClick={() => onPick(null)}>
        <span className="border-status-empty inline-block h-2.5 w-2.5 shrink-0 rounded-full border border-dashed" />
        <span className="truncate">Overview</span>
      </button>
      {sections.map((s, i) => {
        const status = statusOf(s);
        return (
          <button key={s.id} type="button" className={item(shownId === s.id)} onClick={() => onPick(s.id)} title={STATUS_LABEL[status]}>
            <StatusDot status={status} />
            <span className="truncate">
              <span className="tabular-nums">{i + 1}</span> {s.title}
              {s.star ? (
                <span className="text-star ml-1" aria-label="Matters most">
                  ★
                </span>
              ) : null}
            </span>
          </button>
        );
      })}
      {canEdit ? (
        <button type="button" onClick={onAdd} className={cn(item(false), "mt-1")}>
          <Plus size={12} className="shrink-0" />
          <span className="truncate">Add section</span>
        </button>
      ) : null}
    </nav>
  );

  return (
    <div>
      {list}
      <div className="border-border mt-4 border-t pt-4">
        <div className="text-muted-foreground mb-1.5 flex items-center justify-between px-2.5 text-xs font-medium">
          <span>Files for the whole brief</span>
          {canEdit ? (
            <AttachControls briefId={briefId} sectionId={null} compact busy={actions.upload.isPending} onPick={(file, sectionId) => actions.upload.mutate({ file, sectionId })} />
          ) : null}
        </div>
        {looseAttachments.length === 0 ? (
          <p className="text-muted-foreground px-2.5 text-[11px]">{canEdit ? PD_REMINDER : "None."}</p>
        ) : null}
        <div className="px-0.5">
          <AttachmentList briefId={briefId} items={looseAttachments} canEdit={canEdit} busy={actions.deleteAttachment.isPending} onDelete={(id) => actions.deleteAttachment.mutate(id)} />
        </div>
        {actions.upload.isError ? <p className="text-destructive mt-1 px-2.5 text-xs">{(actions.upload.error as Error).message}</p> : null}
      </div>
    </div>
  );
}
